// Production Plan report.
//
// Answers one question for the kitchen: what has to be cooked for a given day.
// Reads the same GetRequestListGen feed the Order List uses, filtered to a
// single delivery date, and turns it into a printable sheet of per-order
// blocks. Defaults to TOMORROW — prep is planned the evening before, so the
// useful default is the next day's orders, not today's.
//
// Self-contained on purpose: the row-reading helpers below are duplicated from
// OrderList rather than shared, so this report can be changed without any risk
// to the order screen.

import React, { Component } from "react";
import { connect } from "react-redux";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import jsPDF from "jspdf";

import * as actions from "../../../store/actions/index";
import { POS_GUEST_SESSION_KEY } from "../../../shared/Config";

// The response flattens the cart into eight fixed slots. An order with more
// than eight distinct items is truncated by the API, not here.
const MAX_ITEM_SLOTS = 8;

// The special-order guest: SpecialOrderDetails.js signs a customer's own phone
// in as 9999, and this page is not for that identity — it signs in as 1001
// instead, exactly as it does when nobody is logged in at all.
const SPECIAL_ORDER_GUEST_EMP_ID = "35ad8f97-36e2-46c6-a602-29b2d2d36f11";

// Mirrors POSApp's own check: a token only counts while it is unexpired.
// EmpId is not a session — it outlives the token by hours, and reading it as
// "somebody is logged in" is what left this page with no identity at all: the
// login was skipped because EmpId was there, POSApp skipped authCheckState
// because the token was dead, so userDetail stayed null and the order API was
// never called. No request, no error, just an empty sheet.
const hasLiveSession = () => {
    if (!localStorage.getItem("token")) return false;
    return new Date(localStorage.getItem("expirationDate")) > new Date();
};

// Most order columns carry a res- prefix, but the money and payment ones do
// not — that asymmetry is the API's, not a typo.
const FIELD_KEYS = {
    orderNum: ["requestNum"],
    customerName: ["rescustomername"],
    phone: ["resphone"],
    address: ["resdeliveryaddress"],
    // resdeliverydate only — no fromDate fallback, unlike the Order List.
    // A production sheet is worked from, so it reports the real delivery date
    // or nothing: an order placed before that column existed has resdeliverydate
    // null and is simply absent from the plan rather than being scheduled off a
    // proxy field that only usually agrees.
    deliveryDate: ["resdeliverydate"],
    deliveryTime: ["resdeliverytime"],
};

const isBlank = (v) => v === undefined || v === null || v === "";

const pick = (row, keys) => {
    if (!row) return null;
    for (const key of keys) {
        if (!isBlank(row[key])) return row[key];
    }
    return null;
};

const DASH = "—";
const PDF_BLANK = "-";

const show = (v) => (isBlank(v) ? DASH : String(v));
const pdfText = (v) => (isBlank(v) ? PDF_BLANK : String(v));

const fmtDate = (value) => {
    if (isBlank(value)) return DASH;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
};

// Local date components, never toISOString(): these timestamps carry no
// timezone suffix so they parse as local midnight, and east of UTC — IST
// included — toISOString would roll that back a day and plan the wrong date.
const dayKey = (value) => {
    if (isBlank(value)) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;

    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
};

const addDays = (date, days) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + days);
    return d;
};

// resmenuitemN holds the menu item's uniqueId (a GUID like
// "d5ef0f15-361a-4596-8d6c-2bbacc45adfc"), NOT its name — the label comes from
// the MenuItem master, which getMasterKeywordList('MenuItem') puts in
// state.keyword.MenuItem as { label: text, value: uniqueId, id }.
//
// Matched on `value`/`uniqueId` only, never on `id`: the master's `id` is 1 for
// a great many rows, so matching it would label items at random. Compared
// case-insensitively, since GUID casing is not consistent across this
// backend's endpoints.
const menuNameFor = (menuItems, menuId) => {
    if (isBlank(menuId)) return null;

    const target = String(menuId).toLowerCase();
    const hit = (menuItems || []).find(
        (m) =>
            String(m.value).toLowerCase() === target ||
            String(m.uniqueId).toLowerCase() === target
    );
    if (hit) return hit.label || hit.text || hit.name || null;

    // Master still loading, or the item has since been removed from it. The
    // short id keeps the line visible and traceable rather than dropping it —
    // a production sheet must never quietly omit something to be cooked.
    return `Item ${String(menuId).slice(0, 8)}`;
};

const getItems = (row, menuItems) => {
    if (!row) return [];

    const items = [];
    for (let i = 1; i <= MAX_ITEM_SLOTS; i += 1) {
        const menuId = row[`resmenuitem${i}`];
        if (isBlank(menuId)) continue;
        // A line reduced to qty 0 stays on the order (RestaurantOrder keeps a
        // saved delivery line at 0 rather than deleting it) but there is nothing
        // to cook, so it is left off the blocks, the totals and the PDF alike.
        // A blank qty is not a 0 and still shows.
        const qty = row[`resmenuqty${i}`];
        if (!isBlank(qty) && Number(qty) === 0) continue;
        items.push({
            name: menuNameFor(menuItems, menuId),
            menuId,
            qty,
        });
    }
    return items;
};

// Total quantity per menu item across the day — the kitchen's actual question,
// which no single order block answers. Screen only; the PDF keeps to the
// per-order blocks that were specified.
const aggregateItems = (orders, menuItems) => {
    const totals = new Map();

    (orders || []).forEach((row) => {
        getItems(row, menuItems).forEach((item) => {
            const qty = Number(item.qty);
            const entry = totals.get(item.name) || { name: item.name, qty: 0, orders: 0 };
            entry.qty += Number.isFinite(qty) ? qty : 0;
            entry.orders += 1;
            totals.set(item.name, entry);
        });
    });

    return [...totals.values()].sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name));
};

// ── PDF geometry (mm, portrait A4) ────────────────────────────────────────
const PAGE_MARGIN = 12;
const BLOCK_PAD = 4;
const BLOCK_GAP = 5;
const HEADER_H = 10;
const CONTENT_TOP = 33; // below the repeated page header

// Font sizes (pt). Sized to be read at arm's length on the pass, so the
// line height and baseline offsets below are derived from FS_BODY.
const FS_TITLE = 18;
const FS_SUB = 11;
const FS_DATE = 14;
const FS_BODY = 12;
const FS_LABEL = 11;
const FS_SECTION = 12.5;
const FS_TH = 11;
const FS_FOOTER = 9;
const LINE_H = 6.2;
const BASELINE = 4.3; // first-line baseline below a row's top, at FS_BODY

const textRight = (doc, text, rightX, y, size) => {
    const width = (doc.getStringUnitWidth(text) * size) / doc.internal.scaleFactor;
    doc.text(text, rightX - width, y);
};

class ProductionPlanReport extends Component {
    state = {
        orders: [],
        // Tomorrow: prep is planned the evening before.
        planDate: addDays(new Date(), 1),
        // Blocks per row in the PDF. 1 and 2 print portrait, 3 prints landscape.
        columns: 2,
    };

    componentDidMount() {

        this.ensureSession();

        const empId = this.props.userDetail && this.props.userDetail.employeeId;
        if (empId) this.fetchOrders(empId);

        // resmenuitemN is a menu-item uniqueId; this master turns it into a
        // name. Cached in localStorage by the action, so this is cheap on
        // every visit after the first.
        this.props.getMasterKeywordList('MenuItem');
    }

    componentDidUpdate(prevProps) {
        const prevEmpId = prevProps.userDetail && prevProps.userDetail.employeeId;
        const currEmpId = this.props.userDetail && this.props.userDetail.employeeId;

        // On any change of identity, not just the first one to arrive: a
        // session re-established after an expiry (see ensureSession) brings
        // the same id back after a null, and a different user brings another
        // one. Keyed on "first appearance" alone, neither refetched.
        if (currEmpId && currEmpId !== prevEmpId) this.fetchOrders(currEmpId);

        if (prevProps.orderList !== this.props.orderList && this.props.orderList) {
            this.setState({ orders: this.props.orderList });
        }
    }

    // Signs this device in as 1001 when it has no usable identity: no live
    // session, or the special-order guest. A live staff session is left
    // alone — auth() overwrites the stored token and EmpId, which would swap
    // a cashier's identity for the guest account mid-shift.
    //
    // Attempted once per visit. userDetail arriving is what triggers the
    // fetch, so a login that fails leaves it null; without this flag the
    // update above would ask for another login on every render.
    // Returns whether a login was started.
    ensureSession = () => {
        if (this.sessionRequested) return false;
        if (hasLiveSession() && localStorage.getItem('EmpId') !== SPECIAL_ORDER_GUEST_EMP_ID) {
            return false;
        }

        this.sessionRequested = true;
        // Marks the session as a guest one, which keeps POSApp from wrapping
        // this page in the staff shell.
        localStorage.setItem(POS_GUEST_SESSION_KEY, "1");
        this.props.onAuth('1001', '456123789', false, false, false);
        return true;
    };

    // Also the way back from a session that expired while this page sat open:
    // there is no employee id left, so fetchOrders would quietly do nothing.
    // Signing in again is enough — the fetch follows from the id arriving.
    handleRefresh = () => {
        this.sessionRequested = false;
        if (this.ensureSession()) return;
        this.fetchOrders();
    };

    fetchOrders = (empId) => {
        const id = empId || (this.props.userDetail && this.props.userDetail.employeeId);
        if (!id) return;

        this.props.getOrderList(
            id,
            "f7405b60-9933-4b08-a8be-107aa21a05f7",
            this.props.userDetail.companyId,
        );
    };

    getPlanOrders = () => {
        const want = dayKey(this.state.planDate);
        if (!want) return [];

        return (this.state.orders || [])
            .filter((row) => dayKey(pick(row, FIELD_KEYS.deliveryDate)) === want)
            // Earliest delivery slot first, so the sheet reads in cooking order.
            //
            // Untimed orders are pushed last by an explicit test rather than by
            // substituting a high sentinel character: localeCompare is ICU
            // collation, which sorts punctuation BEFORE digits, so a "~"
            // placeholder put the untimed orders at the very top instead.
            // Zero-padded "HH:mm" compares correctly with plain < / >, which
            // also keeps locale out of it entirely.
            .sort((a, b) => {
                const ta = pick(a, FIELD_KEYS.deliveryTime);
                const tb = pick(b, FIELD_KEYS.deliveryTime);

                if (isBlank(ta) !== isBlank(tb)) return isBlank(ta) ? 1 : -1;
                if (isBlank(ta)) return 0;

                const sa = String(ta);
                const sb = String(tb);
                if (sa < sb) return -1;
                return sa > sb ? 1 : 0;
            });
    };

    // Says why the sheet is empty when the reason isn't "nothing due that
    // day": nobody signed in on this device, or the fetch itself failed. Both
    // used to render as a plain empty sheet, indistinguishable from a quiet
    // day, which is how an expired session went unnoticed for hours.
    renderProblemNotice = () => {
        if (this.props.loading) return null;

        const empId = this.props.userDetail && this.props.userDetail.employeeId;
        const message = !empId
            ? "Not signed in on this device, so no orders could be loaded."
            // The fetch error is only worth showing while there is nothing to
            // show: with orders loaded it is an older failure the page has
            // already recovered from.
            : (!this.state.orders.length && this.props.error ? String(this.props.error) : null);

        if (!message) return null;

        return (
            <div className="pp-notice">
                <span className="pp-notice-text">{message}</span>
                <Button
                    label={empId ? "Retry" : "Sign in again"}
                    icon="pi pi-refresh"
                    className="p-button-sm p-button-outlined"
                    onClick={this.handleRefresh}
                />
            </div>
        );
    };

    generatedBy = () => {
        const d = this.props.userDetail || {};
        const u = this.props.userObject || {};
        const joined = [d.firstName, d.lastName].filter(Boolean).join(" ").trim();
        return d.EmployeeName || joined || u.EmployeeName || u.userName || "Unknown user";
    };

    // One order block. Called twice per order — once with draw=false purely to
    // measure, once to paint — so the height used for the page-break decision
    // is by construction the height actually drawn.
    renderBlock = (doc, row, x, y, width, draw, minHeight) => {
        const innerX = x + BLOCK_PAD;
        const innerRight = x + width - BLOCK_PAD;
        const innerWidth = width - BLOCK_PAD * 2;
        let cursor = y;

        // Header — the delivery date, with the order number alongside it so a
        // block on the pass can be traced back to its order.
        if (draw) {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(FS_DATE);
            doc.setTextColor(0);
            doc.text(fmtDate(pick(row, FIELD_KEYS.deliveryDate)), innerX, cursor + 7);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(FS_BODY);
            textRight(doc, pdfText(pick(row, FIELD_KEYS.orderNum)), innerRight, cursor + 7, FS_BODY);

            doc.setDrawColor(120);
            doc.setLineWidth(0.1);
            doc.line(x, cursor + HEADER_H, x + width, cursor + HEADER_H);
        }
        cursor += HEADER_H;

        // Delivery time / Name / Phone / Address.
        //
        // The label gutter is capped as a fraction of the block so a 3-up
        // layout doesn't spend half its width on the word "Delivery time" and
        // wrap every address to five lines.
        const LABEL_W = Math.min(28, innerWidth * 0.35);
        const rows = [
            ["Delivery time", pdfText(pick(row, FIELD_KEYS.deliveryTime))],
            ["Name", pdfText(pick(row, FIELD_KEYS.customerName))],
            ["Phone", pdfText(pick(row, FIELD_KEYS.phone))],
            ["Address", pdfText(pick(row, FIELD_KEYS.address))],
        ];

        cursor += 3;
        rows.forEach(([label, value]) => {
            // The font is set on BOTH passes, never only inside `if (draw)`.
            // splitTextToSize measures against jsPDF's current font, so leaving
            // it to whatever the previous call happened to set made the measure
            // pass wrap a long address differently from the draw pass — the
            // block was then measured shorter than it was painted and ran over
            // the one below it.
            doc.setFont("helvetica", "normal");
            doc.setFontSize(FS_BODY);
            const lines = doc.splitTextToSize(value, innerWidth - LABEL_W);

            if (draw) {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(FS_LABEL);
                doc.text(`${label}`, innerX, cursor + BASELINE);
                doc.setFont("helvetica", "normal");
                doc.setFontSize(FS_BODY);
                doc.text(lines, innerX + LABEL_W, cursor + BASELINE, { lineHeightFactor: LINE_H / (FS_BODY * 0.3528) });
            }
            cursor += Math.max(1, lines.length) * LINE_H;
        });

        // Order Menu.
        cursor += 3;
        if (draw) {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(FS_SECTION);
            doc.text("Order Menu", innerX, cursor + 4.8);
        }
        cursor += 7.5;

        const QTY_W = 22;
        if (draw) {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(FS_TH);
            doc.text("Menu Item", innerX, cursor + 4);
            textRight(doc, "Qty", innerRight, cursor + 4, FS_TH);
            doc.setDrawColor(170);
            doc.line(innerX, cursor + 5.8, innerRight, cursor + 5.8);
        }
        cursor += 7.8;

        const items = getItems(row, this.props.MenuItem);

        if (!items.length) {
            if (draw) {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(FS_BODY);
                doc.text("No items recorded against this order.", innerX, cursor + BASELINE);
            }
            cursor += LINE_H;
        } else {
            items.forEach((item) => {
                // Same reason as the meta rows above: measure under a font this
                // pass set itself, not an inherited one.
                doc.setFont("helvetica", "normal");
                doc.setFontSize(FS_BODY);
                const lines = doc.splitTextToSize(item.name, innerWidth - QTY_W);

                if (draw) {
                    doc.setFont("helvetica", "normal");
                    doc.text(lines, innerX, cursor + BASELINE, { lineHeightFactor: LINE_H / (FS_BODY * 0.3528) });
                    doc.setFont("helvetica", "bold");
                    textRight(doc, pdfText(item.qty), innerRight, cursor + BASELINE, FS_BODY);
                }
                cursor += Math.max(1, lines.length) * LINE_H;
            });
        }

        cursor += BLOCK_PAD;

        const height = cursor - y;
        if (draw) {
            // Every block in a row is boxed to the tallest of them, so the row
            // reads as a row rather than as ragged cards of different depths.
            doc.setDrawColor(120);
            doc.setLineWidth(0.2);
            doc.rect(x, y, width, Math.max(height, minHeight || 0));
        }
        return height;
    };

    buildPdf = () => {
        const orders = this.getPlanOrders();
        if (!orders.length) return null;

        const columns = [1, 2, 3].includes(this.state.columns) ? this.state.columns : 2;

        // Orientation follows the column count so a block stays about 88-90mm
        // wide either way. Three portrait columns would be ~59mm each, which
        // wraps every address into a five-line sliver; landscape gives three
        // columns the same width two portrait columns get.
        const doc = new jsPDF(columns === 3 ? "l" : "p", "mm", "a4");
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.getWidth ? pageSize.getWidth() : pageSize.width;
        const pageHeight = pageSize.getHeight ? pageSize.getHeight() : pageSize.height;

        const usableWidth = pageWidth - PAGE_MARGIN * 2;
        const bottomLimit = pageHeight - PAGE_MARGIN - 8;
        const planLabel = this.state.planDate.toLocaleDateString();

        const drawPageHeader = () => {
            doc.setTextColor(0);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(FS_TITLE);
            doc.text("Production Plan", PAGE_MARGIN, 16);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(FS_SUB);
            doc.text(`Preparation date ${planLabel}  |  ${orders.length} order(s)`, PAGE_MARGIN, 22.5);
            doc.text(
                `Generated by ${this.generatedBy()} on ${new Date().toLocaleString()}`,
                PAGE_MARGIN,
                27.5,
            );

            doc.setDrawColor(120);
            doc.setLineWidth(0.1);
            doc.line(PAGE_MARGIN, 30, pageWidth - PAGE_MARGIN, 30);
        };

        drawPageHeader();
        let cursorY = CONTENT_TOP;

        const blockWidth = (usableWidth - BLOCK_GAP * (columns - 1)) / columns;

        // Laid out a row at a time rather than block by block: a row's height is
        // its tallest block, and the page-break test has to be made against that
        // height for the whole row at once — deciding per block would break a
        // row in half across the fold.
        for (let start = 0; start < orders.length; start += columns) {
            const rowOrders = orders.slice(start, start + columns);

            const heights = rowOrders.map((row, i) =>
                this.renderBlock(
                    doc,
                    row,
                    PAGE_MARGIN + i * (blockWidth + BLOCK_GAP),
                    cursorY,
                    blockWidth,
                    false,
                ),
            );
            const rowHeight = Math.max(...heights);

            // A row that would straddle the fold starts the next page whole —
            // half an order's items on each side of a page break is exactly the
            // way a kitchen sheet gets misread. The cursorY test also stops a
            // row taller than a whole page from looping forever: it is drawn
            // as-is on a fresh page instead.
            if (cursorY + rowHeight > bottomLimit && cursorY > CONTENT_TOP) {
                doc.addPage();
                drawPageHeader();
                cursorY = CONTENT_TOP;
            }

            rowOrders.forEach((row, i) => {
                this.renderBlock(
                    doc,
                    row,
                    PAGE_MARGIN + i * (blockWidth + BLOCK_GAP),
                    cursorY,
                    blockWidth,
                    true,
                    rowHeight,
                );
            });

            cursorY += rowHeight + BLOCK_GAP;
        }

        // Stamped after the fact: during the run the total page count is not
        // known yet, so every page would read "Page 1 of 1".
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i += 1) {
            doc.setPage(i);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(FS_FOOTER);
            doc.setTextColor(0);
            textRight(doc, `Page ${i} of ${pageCount}`, pageWidth - PAGE_MARGIN, pageHeight - 8, FS_FOOTER);
        }

        return doc;
    };

    downloadPdf = () => {
        const doc = this.buildPdf();
        if (doc) doc.save(`production-plan-${dayKey(this.state.planDate)}.pdf`);
    };

    printPdf = () => {
        const doc = this.buildPdf();
        if (!doc) return;

        // autoPrint makes the viewer raise its print dialog on open, so this is
        // one action for the user rather than download-then-open-then-print.
        doc.autoPrint();
        const url = doc.output("bloburl");
        const win = window.open(url, "_blank");
        if (!win) {
            // Popup blocked — fall back to a download so the click still does
            // something rather than failing silently.
            doc.save(`production-plan-${dayKey(this.state.planDate)}.pdf`);
        }
    };

    renderBlockPreview = (row, index) => {
        const items = getItems(row, this.props.MenuItem);

        return (
            <div className="pp-block" key={row.requestId || index}>
                <div className="pp-block-head">
                    <span className="pp-date">{fmtDate(pick(row, FIELD_KEYS.deliveryDate))}</span>
                    <span className="pp-ordernum">{show(pick(row, FIELD_KEYS.orderNum))}</span>
                </div>

                <div className="pp-meta">
                    <div><span>Delivery time</span>{show(pick(row, FIELD_KEYS.deliveryTime))}</div>
                    <div><span>Name</span>{show(pick(row, FIELD_KEYS.customerName))}</div>
                    <div><span>Phone</span>{show(pick(row, FIELD_KEYS.phone))}</div>
                    <div><span>Address</span>{show(pick(row, FIELD_KEYS.address))}</div>
                </div>

                <div className="pp-menu-head">Order Menu</div>
                {items.length ? (
                    <table className="pp-table">
                        <thead>
                            <tr><th>Menu Item</th><th className="pp-n">Qty</th></tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => (
                                <tr key={`${item.name}-${i}`}>
                                    <td>{item.name}</td>
                                    <td className="pp-n">{show(item.qty)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="pp-noitems">No items recorded against this order.</div>
                )}
            </div>
        );
    };

    render() {
        const orders = this.getPlanOrders();
        const totals = aggregateItems(orders, this.props.MenuItem);

        return (
            <div className="pp-root">
                <style>{STYLES}</style>

                {this.props.loading && <div className="page-loader" />}

                <div className="pp-bar">
                    <h2 className="pp-title">Production Plan</h2>
                    <div className="pp-bar-actions">
                        <Button
                            icon="pi pi-file-pdf"
                            label="Download"
                            className="p-button-sm p-button-outlined"
                            disabled={!orders.length}
                            onClick={this.downloadPdf}
                        />
                        <Button
                            icon="pi pi-print"
                            label="Print"
                            className="p-button-sm p-button-outlined"
                            disabled={!orders.length}
                            onClick={this.printPdf}
                        />
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-sm p-button-outlined"
                            onClick={this.handleRefresh}
                        />
                    </div>
                </div>

                <div className="pp-filters">
                    <div className="pp-date">
                        <Calendar
                            inputId="pp-plan-date"
                            value={this.state.planDate}
                            onChange={(e) =>
                                this.setState({ planDate: e.value || addDays(new Date(), 1) })
                            }
                            dateFormat="dd/mm/yy"
                            placeholder="Preparation date"
                            showIcon
                            readOnlyInput
                        />
                    </div>
                    <Button
                        label="Tomorrow"
                        className="p-button-sm p-button-outlined"
                        onClick={() => this.setState({ planDate: addDays(new Date(), 1) })}
                    />
                    <Button
                        label="Today"
                        className="p-button-sm p-button-outlined"
                        onClick={() => this.setState({ planDate: addDays(new Date(), 0) })}
                    />
                    <div className="pp-cols">
                        <span>Blocks per row</span>
                        {[1, 2, 3].map((n) => (
                            <Button
                                key={n}
                                label={String(n)}
                                className={`p-button-sm ${
                                    this.state.columns === n ? "" : "p-button-outlined"
                                }`}
                                onClick={() => this.setState({ columns: n })}
                            />
                        ))}
                    </div>

                    <div className="pp-count">
                        {orders.length} order(s) for {this.state.planDate.toLocaleDateString()}
                        {" · PDF prints "}
                        {this.state.columns} per row (
                        {this.state.columns === 3 ? "landscape" : "portrait"} A4)
                    </div>
                </div>

                {/* Screen-only: the day's totals per item. The PDF keeps to the
                    per-order blocks that were asked for. */}
                {totals.length ? (
                    <div className="pp-summary">
                        <div className="pp-summary-head">Total to prepare</div>
                        <div className="pp-chips">
                            {totals.map((t) => (
                                <span className="pp-chip" key={t.name}>
                                    {t.name}<b>{t.qty}</b>
                                </span>
                            ))}
                        </div>
                    </div>
                ) : null}

                {this.renderProblemNotice()}

                {orders.length ? (
                    <div className="pp-grid">{orders.map(this.renderBlockPreview)}</div>
                ) : (
                    <div className="pp-empty">
                        No orders due on {this.state.planDate.toLocaleDateString()}.
                    </div>
                )}
            </div>
        );
    }
}

// Scoped to .pp-root. The preview blocks deliberately mirror the PDF block
// layout, so what is on screen is what comes out of the printer.
// Chenthoor palette, mirrored from src/POS/styles/_variables.scss — fanta
// orange as the primary accent over a warm off-white. Redeclared as custom
// properties because this page styles itself from a JS string and cannot read
// the SCSS variables; if the theme moves, these --pp-* values are the whole
// surface to update.
//
// Note .pp-date is doing double duty in the markup — the filter wrapper and
// the date inside a block header — so each rule is qualified by its parent
// rather than relying on source order.
const STYLES = `
.pp-root {
  --pp-accent:#ff5a1f; --pp-accent-dark:#e04711; --pp-accent-soft:#fff0e9;
  --pp-ink:#2b2119; --pp-muted:#8a7b70; --pp-line:#ece3db;
  --pp-surface:#fff; --pp-ground:#faf7f4;

  background:var(--pp-ground); min-height:100%; padding:14px; color:var(--pp-ink);
  -webkit-font-smoothing:antialiased;
}

.pp-bar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.pp-title { font-size:20px; font-weight:800; margin:0; flex:1 1 auto; letter-spacing:-.02em; }
.pp-title:before {
  content:""; display:block; width:34px; height:3px; border-radius:3px;
  background:linear-gradient(90deg,var(--pp-accent),var(--pp-accent-dark)); margin-bottom:7px;
}
.pp-bar-actions { display:flex; gap:8px; flex:0 0 auto; }
.pp-bar-actions .p-button { border-radius:10px; }

.pp-filters { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-bottom:14px; }
.pp-filters > .pp-date { flex:1 1 170px; min-width:0; }
.pp-filters .p-calendar, .pp-filters .p-inputtext { width:100%; }
.pp-filters .p-inputtext { border-radius:11px; border-color:var(--pp-line); }
.pp-filters .p-inputtext:enabled:focus { box-shadow:0 0 0 3px rgba(255,90,31,.16); border-color:var(--pp-accent); }
.pp-filters .p-button { border-radius:10px; }
.pp-cols { display:flex; gap:6px; align-items:center; }
.pp-cols span { font-size:12px; color:var(--pp-muted); margin-right:2px; font-weight:600; }
.pp-cols .p-button { min-width:42px; }
.pp-count { font-size:12px; color:var(--pp-muted); flex:1 1 100%; }

.pp-summary {
  background:linear-gradient(135deg,#fff 0%,var(--pp-accent-soft) 100%);
  border:1px solid #fadcce; border-radius:16px; padding:14px; margin-bottom:14px;
  box-shadow:0 1px 2px rgba(43,33,25,.04);
}
.pp-summary-head { font-size:10.5px; text-transform:uppercase; letter-spacing:.06em;
                   color:#a8522e; font-weight:700; margin-bottom:10px; }
.pp-chips { display:flex; flex-wrap:wrap; gap:7px; }
.pp-chip {
  background:var(--pp-surface); border:1px solid #f6d9c9; border-radius:999px;
  padding:5px 7px 5px 12px; font-size:13px; color:#5a4b40; display:inline-flex;
  align-items:center; gap:8px; box-shadow:0 1px 2px rgba(43,33,25,.04);
}
/* The number is the point of the chip, so it gets the accent pill. */
.pp-chip b {
  margin:0; font-size:12.5px; font-weight:800; color:#fff; min-width:22px; text-align:center;
  background:linear-gradient(135deg,var(--pp-accent),var(--pp-accent-dark));
  border-radius:999px; padding:2px 8px; font-variant-numeric:tabular-nums;
}

.pp-grid { display:grid; grid-template-columns:1fr; gap:12px; align-items:start; }

.pp-block {
  background:var(--pp-surface); border:1px solid var(--pp-line); border-radius:16px;
  overflow:hidden; box-shadow:0 1px 2px rgba(43,33,25,.04);
  transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}
.pp-block:hover { transform:translateY(-2px); border-color:#f6d9c9;
                  box-shadow:0 10px 26px -14px rgba(43,33,25,.28); }
.pp-block-head {
  display:flex; align-items:center; justify-content:space-between; gap:8px;
  padding:11px 14px; border-bottom:1px solid var(--pp-line);
  background:linear-gradient(135deg,var(--pp-accent-soft) 0%,#fff 100%);
}
.pp-block .pp-date { font-size:15px; font-weight:800; letter-spacing:-.01em; color:#7a3d22; }
.pp-ordernum { font-size:11.5px; color:var(--pp-muted); word-break:break-all;
               font-variant-numeric:tabular-nums; }

.pp-meta { padding:11px 14px 5px; }
.pp-meta > div { display:flex; gap:10px; font-size:13px; color:#5a4b40; margin-bottom:6px; align-items:flex-start; }
.pp-meta span { flex:0 0 96px; font-weight:700; color:var(--pp-ink); font-size:11.5px;
                text-transform:uppercase; letter-spacing:.04em; padding-top:1px; }

.pp-menu-head { padding:6px 14px 0; font-size:11px; font-weight:800; text-transform:uppercase;
                letter-spacing:.06em; color:var(--pp-accent-dark); }
.pp-table { width:100%; border-collapse:collapse; font-size:13px; margin:8px 0 12px; }
.pp-table th { text-align:left; font-size:10.5px; text-transform:uppercase; letter-spacing:.06em;
               color:var(--pp-muted); font-weight:700; padding:6px 14px; border-bottom:1px solid var(--pp-line); }
.pp-table td { padding:7px 14px; border-bottom:1px solid #f7f2ed; }
.pp-table tr:last-child td { border-bottom:none; }
.pp-table .pp-n { text-align:right; white-space:nowrap; font-weight:700;
                  font-variant-numeric:tabular-nums; }
.pp-noitems { padding:8px 14px 14px; font-size:12.5px; color:var(--pp-muted); font-style:italic; }

.pp-empty { background:var(--pp-surface); border:1px dashed var(--pp-line); border-radius:16px;
            padding:44px 16px; text-align:center; color:var(--pp-muted); }
/* Problem notice: a failed fetch and a missing session both used to look
   exactly like a day with nothing to cook, so neither was ever noticed. */
.pp-notice { display:flex; flex-wrap:wrap; align-items:center; gap:10px; margin-bottom:14px;
             background:#fff4f2; border:1px solid #f0c5bb; border-radius:14px;
             padding:12px 14px; color:#8a2b17; font-size:13px; line-height:1.45; }
.pp-notice-text { flex:1 1 200px; min-width:0; }

@media (min-width:640px) {
  .pp-root { padding:18px; }
  .pp-title { font-size:23px; }
  .pp-grid { grid-template-columns:repeat(2,1fr); }
}
@media (min-width:1200px) {
  .pp-root { padding:22px 26px; }
  .pp-grid { grid-template-columns:repeat(3,1fr); }
}

/* Respect a reduced-motion preference: the lift is decoration. */
@media (prefers-reduced-motion:reduce) {
  .pp-block { transition:none; }
  .pp-block:hover { transform:none; }
}
`;

const mapStateToProps = (state) => ({
    loading: state.master.loading,
    // Shown by renderProblemNotice when the sheet is empty and the fetch is
    // what failed.
    error: state.master.error,
    orderList: state.master.requestlistgendata,
    userDetail: state.auth.userDetail,
    userObject: state.auth.userObject,
    // Menu-item id -> name lookup for the resmenuitemN columns.
    MenuItem: state.keyword.MenuItem !== undefined ? state.keyword.MenuItem : [],
});

const mapDispatchToProps = (dispatch) => ({
    getOrderList: (id, processId, companyId) =>
        dispatch(actions.getrequestListgen(id, processId, companyId)),
    getMasterKeywordList: (keyword) => dispatch(actions.getMasterKeywordList(keyword)),
    onAuth: (u, p, isSignup, Ar, mobile) => dispatch(actions.auth(u, p, isSignup, Ar, mobile)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductionPlanReport);
